import { assertUnreachable, isDefined, isNotDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { clsx } from 'clsx';
import { type RefObject, useEffect, useRef } from 'react';

import { optionApi } from '@/api/option';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { FormattedPremium } from '@/App/tiles/Option/utils/FormattedPremium.tsx';
import { getAssociatedPremiumCategory, getPremiumFromQuote } from '@/App/tiles/Option/utils/premiumUtils.ts';
import { ForbiddenExecutionTooltip } from '@/App/tiles/shared/ForbiddenExecutionTooltip.tsx';
import { checkIfUserCanTrade, useUserContext } from '@/context/UserContext.tsx';
import { getUserEmail } from '@/sgwt/sgwtConnect.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { getQuote, getRfsId, selectOptionQuoteLegs } from '@/store/option/selectors/optionSelectors.ts';
import type { FxOption, LegId, PremiumType } from '@/store/option/state.ts';
import { createAppAsyncThunk } from '@/store/storeMethods.ts';
import { getIsStreamingConnected } from '@/store/streaming/slice.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import type { Way } from '@/types/common.ts';
import { getRateComponentStyling } from '@/utils/rateComponentStyling.ts';

import { OptionWayMessage } from './OptionWayMessage.tsx';
import { PremiumWayMessage } from './PremiumWayMessage.tsx';

type RateProps = {
  tileId: string;
  way: Way;
  executable: boolean;
};

export function Rate({ tileId, way, executable }: RateProps) {
  const dispatch = useAppDispatch();
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const hasTradePermission = useCanTrade();
  const [outer, inner] = useScaleDown<HTMLDivElement, HTMLDivElement>();

  const legId: LegId = `${tileId}/0`;

  const oneWayQuotation = useAppSelector((state) => state.userPreferences.optionOneWayQuotation);
  const showColoredPrices = useAppSelector((state) => state.userPreferences.showColoredPrices);
  const {
    premiumAmount,
    premiumMessage,
    associatedPremiumAmount,
    premiumType,
    associatedPremiumType,
    isExpired,
    isClientWay,
    strategyPanelVisible,
    realWay,
    isTcReady,
  } = useOptionSelector((option) => {
    const premiumType = option.properties.premiumType;
    const premium = getPremiumFromQuote(premiumType, option, legId);
    const associatedPremiumType = {
      premiumCurrency: premiumType.premiumCurrency,
      premiumCategory: getAssociatedPremiumCategory(premiumType.premiumCategory),
    };
    const associatedPremium = getPremiumFromQuote(associatedPremiumType, option, legId);
    const strategyPanelVisible = option.properties.uiState.strategyPanelMode !== 'hidden';
    const majorPremiumIsMissing = isPotentialMajorValueUnavailable(option, legId, premiumType);
    const realWay = premium.isReversed ? reverseWay(way) : way;
    const isClientWay = realWay === 'Ask';
    const premiumIsNegative = way === 'Bid' ? premium.bidIsNegative : premium.askIsNegative;
    const messageWay: Way = premiumIsNegative ? reverseWay(realWay) : realWay;
    const premiumMessage = messageWay === 'Bid' ? ('option.way.receive' as const) : ('option.way.pay' as const);

    return {
      premiumAmount: majorPremiumIsMissing
        ? way === 'Bid'
          ? associatedPremium.bid
          : associatedPremium.ask
        : way === 'Bid'
          ? premium.bid
          : premium.ask,
      premiumMessage,
      premiumType: majorPremiumIsMissing ? associatedPremiumType : premiumType,
      associatedPremiumAmount: majorPremiumIsMissing
        ? undefined
        : way === 'Bid'
          ? associatedPremium.bid
          : associatedPremium.ask,
      associatedPremiumType: majorPremiumIsMissing ? undefined : associatedPremiumType,
      isExpired: option.properties.rfs.status === 'EXPIRED',
      isClientWay,
      strategyPanelVisible,
      realWay,
      isTcReady: option.tradeCapture.status === 'CHECKED',
    };
  });

  const showOneWay = oneWayQuotation && strategyPanelVisible;
  const wrongWay = !isClientWay && showOneWay;
  const canTrade = hasTradePermission && !wrongWay;
  const isReadyExecutable = executable && isTcReady;

  function handleExecution() {
    sgwtWebAnalytics?.trackEvent('Option', 'Execution', realWay);
    dispatch(executeOptionThunk({ tileId, way: realWay }));
  }

  return (
    <div ref={outer} className={clsx('w-50', { 'border-end': way === 'Bid' })}>
      <ForbiddenExecutionTooltip placement={way === 'Bid' ? 'left' : 'right'}>
        <div
          className={clsx(
            'd-flex flex-column justify-content-between px-2 py-1 h-100',
            {
              expired: isExpired,
              bid: way === 'Bid',
              ask: way === 'Ask',
              'colored-prices': showColoredPrices,
            },
            ...getRateComponentStyling({ executable: isReadyExecutable && !isExpired, cantTrade: !canTrade }),
            !isClientWay && strategyPanelVisible && 'text-secondary',
          )}
          onDoubleClick={canTrade && isReadyExecutable ? () => handleExecution() : undefined}
          data-e2e={`premium-${way.toLowerCase()}`}
          data-e2e-rate-type={isReadyExecutable ? 'executable' : 'indicative'}
          data-e2e-can-trade={canTrade}
        >
          <PremiumWayMessage data-e2e={`premium-${way.toLowerCase()}-label`} labelId={premiumMessage} />

          <div className="fs-12 lh-1" data-e2e={`associated-${way.toLowerCase()}-price`}>
            {isDefined(associatedPremiumAmount) &&
              isDefined(associatedPremiumType) &&
              associatedPremiumAmount !== 0 && (
                <FormattedPremium premiumAmount={associatedPremiumAmount} premiumType={associatedPremiumType} />
              )}
          </div>

          <div ref={inner} data-e2e={`premium-${way.toLowerCase()}-value`} className="align-self-center p-2">
            <FormattedPremium className="h3" premiumAmount={premiumAmount} premiumType={premiumType} />
          </div>

          <OptionWayMessage data-e2e={`premium${way}-way-label`} way={realWay} />
        </div>
      </ForbiddenExecutionTooltip>
    </div>
  );
}

function useCanTrade(): boolean {
  const userContextValue = useUserContext();
  const userCanTrade = isDefined(userContextValue) ? checkIfUserCanTrade(userContextValue) : false;
  const isStreamingConnected = useAppSelector(getIsStreamingConnected);

  return userCanTrade && isStreamingConnected;
}

function useScaleDown<Outer extends HTMLElement = HTMLElement, Inner extends HTMLElement = HTMLElement>(): [
  RefObject<Outer | null>,
  RefObject<Inner | null>,
] {
  const outerRef = useRef<Outer>(null);
  const innerRef = useRef<Inner>(null);

  useEffect(() => {
    if (outerRef.current !== null && innerRef.current !== null) {
      const outer = outerRef.current.clientWidth;
      const inner = innerRef.current.clientWidth;

      if (inner > outer) {
        innerRef.current.style.transform = `scale(${outer / inner})`;
      } else {
        innerRef.current.style.transform = '';
      }
    }
  });

  return [outerRef, innerRef];
}

function isPotentialMajorValueUnavailable(option: FxOption, legId: LegId, premiumType: PremiumType): boolean {
  const quote = getQuote(option);

  switch (premiumType.premiumCategory) {
    case 'AMOUNT': {
      if (isNotDefined(quote)) {
        return true;
      }

      const premium = premiumType.premiumCurrency === 1 ? quote.ccy1 : quote.ccy2;
      return hasNoDisplayedValue(premium.bidAmount, premium.askAmount);
    }
    case 'PERCENT': {
      if (isNotDefined(quote)) {
        return true;
      }

      const premium = premiumType.premiumCurrency === 1 ? quote.ccy1 : quote.ccy2;
      return hasNoDisplayedValue(premium.bidPercent, premium.askPercent);
    }
    case 'PPS': {
      if (isNotDefined(quote)) {
        return true;
      }

      const premium = premiumType.premiumCurrency === 1 ? quote.ccy1 : quote.ccy2;
      return hasNoDisplayedValue(premium.bidPps, premium.askPps);
    }
    case 'VOLATILITY': {
      const volatility = selectOptionQuoteLegs(option, legId)?.marketData?.volatility;
      return isNotDefined(volatility) || hasNoDisplayedValue(volatility.bid, volatility.ask);
    }
    default:
      return assertUnreachable(premiumType.premiumCategory, `Unknown premium category: ${premiumType.premiumCategory}`);
  }
}

function hasNoDisplayedValue(bid: number | null | undefined, ask: number | null | undefined): boolean {
  return (bid === 0 && ask === 0) || (isNotDefined(bid) && isNotDefined(ask));
}

function reverseWay(way: Way): Way {
  return way === 'Bid' ? 'Ask' : 'Bid';
}

const executeOptionThunk = createAppAsyncThunk<void, { tileId: string; way: 'Bid' | 'Ask' }>(
  'option/executeOptionRfs',
  async (payload, { getState, dispatch }) => {
    const { tileId, way } = payload;
    const state = getState();
    const option = state.option[tileId];

    if (isNotDefined(option)) {
      return;
    }

    const preferredEmails = state.userPreferences.splitNotifications
      ? state.userPreferences.splitNotificationsEmailsOptions
      : state.userPreferences.emails;
    const clientId = selectTileClientId(state, tileId);
    const notificationEmails = preferredEmails.length > 0 ? preferredEmails.join(';') : getUserEmail();
    const rfsId = getRfsId(option);
    const quote = getQuote(option);
    const quoteId = quote?.quoteId;

    if (
      isNotDefined(quoteId) ||
      isNotDefined(rfsId) ||
      isNotDefined(clientId) ||
      isNotDefined(notificationEmails) ||
      isNotDefined(quote)
    ) {
      return;
    }

    dispatch(
      optionApi.endpoints.executeOptionRfs.initiate(
        {
          notificationEmails,
          tileId,
          quoteId,
          accountId: String(clientId),
          rfsId,
          way,
          lastQuoteReceivedByFrontTimestamp: quote.frontTimestamp,
          lastQuoteFromBackTimestamp: quote.backTimestamp,
        },
        { fixedCacheKey: rfsId },
      ),
    );
  },
);
